import * as React from "react"
import { Link } from "react-router-dom"
import { ArrowRight, CheckCircle2, Package, Power, PowerOff, Trash2, Upload } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useConfirm } from "@/components/confirm-dialog"
import {
  ALLOWED_PACKAGES,
  deleteBuild,
  getUpdatePolicy,
  listBuilds,
  promoteBuild,
  setUpdatePolicy,
  uploadBuild,
  type AppBuild,
  type UpdatePolicy,
  type UploadProgress,
} from "@/lib/api"
import { formatAbsolute, formatBytes } from "@/lib/format"

export function BuildsPage() {
  const confirm = useConfirm()
  const [builds, setBuilds] = React.useState<AppBuild[] | null>(null)
  const [busy, setBusy] = React.useState(false)

  const load = React.useCallback(async () => {
    try {
      setBuilds(await listBuilds())
    } catch (err) {
      toast.error(`טעינת הגרסאות נכשלה: ${(err as Error).message}`)
    }
  }, [])

  React.useEffect(() => {
    load()
  }, [load])

  async function onPromote(build: AppBuild) {
    const ok = await confirm({
      title: `להפוך את ${build.version} לגרסה הנוכחית?`,
      description:
        "כל המכשירים שעדיין לא בגרסה הזו יעדכנו אליה בחלון העדכון הלילי הבא. " +
        "הגרסה שסומנה קודם תפסיק להיות מוצעת.",
      confirmLabel: "סמן כנוכחית",
    })
    if (!ok) return
    setBusy(true)
    try {
      await promoteBuild(build.id)
      toast.success(`${build.version} סומנה כגרסה הנוכחית`)
      await load()
    } catch (err) {
      toast.error(`הסימון נכשל: ${(err as Error).message}`)
    } finally {
      setBusy(false)
    }
  }

  async function onDelete(build: AppBuild) {
    const ok = await confirm({
      title: `למחוק את ${build.version}?`,
      description: "קובץ ה-APK יימחק מהשרת. פעולה בלתי הפיכה.",
      confirmLabel: "מחק",
      destructive: true,
    })
    if (!ok) return
    setBusy(true)
    try {
      await deleteBuild(build.id)
      toast.success("הגרסה נמחקה")
      await load()
    } catch (err) {
      const msg = (err as Error).message
      toast.error(
        msg === "is_current"
          ? "אי אפשר למחוק את הגרסה הנוכחית - סמן גרסה אחרת קודם"
          : `המחיקה נכשלה: ${msg}`,
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="icon-sm">
          <Link to="/">
            <ArrowRight className="size-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-xl font-semibold">גרסאות אפליקציה</h1>
          <p className="text-sm text-muted-foreground">
            הגרסה המסומנת כ"נוכחית" היא מה שהמכשירים יתקינו בחלון העדכון הלילי שלהם.
            להתקנה מיידית על מכשיר בודד - היכנס לעמוד המכשיר.
          </p>
        </div>
      </div>

      <PolicyCard />

      <UploadCard busy={busy} setBusy={setBusy} onUploaded={load} />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="size-4" />
            גרסאות שהועלו
          </CardTitle>
        </CardHeader>
        <CardContent>
          {builds === null ? (
            <div className="text-sm text-muted-foreground">טוען…</div>
          ) : builds.length === 0 ? (
            <div className="text-sm text-muted-foreground">עדיין לא הועלה אף APK.</div>
          ) : (
            <div className="space-y-2">
              {builds.map((b) => (
                <div
                  key={b.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3"
                >
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{b.version}</span>
                      {b.versionCode ? (
                        <span className="text-xs text-muted-foreground">({b.versionCode})</span>
                      ) : null}
                      {b.isCurrent ? (
                        <Badge variant="default" className="gap-1">
                          <CheckCircle2 className="size-3" />
                          נוכחית
                        </Badge>
                      ) : null}
                      <Badge variant="outline">{b.packageName}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatBytes(b.sizeBytes)} · הועלה {formatAbsolute(b.createdAt)}
                      {b.uploadedBy ? ` · ${b.uploadedBy}` : ""}
                    </div>
                    {b.releaseNotes ? (
                      <div className="text-xs whitespace-pre-wrap">{b.releaseNotes}</div>
                    ) : null}
                    <div className="font-mono text-[10px] break-all text-muted-foreground" dir="ltr">
                      sha256: {b.sha256}
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {!b.isCurrent && (
                      <Button size="xs" variant="outline" disabled={busy} onClick={() => onPromote(b)}>
                        סמן כנוכחית
                      </Button>
                    )}
                    <Button
                      size="xs"
                      variant="ghost"
                      disabled={busy || b.isCurrent}
                      onClick={() => onDelete(b)}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

/**
 * מתג הכיבוי הגלובלי. הוא לא נוגע בהגדרות של המכשירים - הוא פשוט גורם למניפסט
 * לחזור ריק, כך שגם מכשיר עם חלון עדכון פעיל לא ימצא מה להוריד.
 */
function PolicyCard() {
  const confirm = useConfirm()
  const [policy, setPolicy] = React.useState<UpdatePolicy | null>(null)
  const [busy, setBusy] = React.useState(false)

  React.useEffect(() => {
    getUpdatePolicy()
      .then(setPolicy)
      .catch((err) => toast.error(`טעינת מצב העדכונים נכשלה: ${(err as Error).message}`))
  }, [])

  async function toggle(next: boolean) {
    if (!next) {
      const ok = await confirm({
        title: "להשבית את מנגנון העדכון לכל המכשירים?",
        description:
          "המניפסט יחזור ריק, ולכן אף מכשיר לא יוריד גרסה חדשה - גם אם סומנה גרסה נוכחית " +
          "וגם אם חלון העדכון של המכשיר פעיל. התקנה נקודתית ממסך המכשיר עדיין תעבוד.",
        confirmLabel: "השבת עדכונים",
        destructive: true,
      })
      if (!ok) return
    }
    setBusy(true)
    try {
      setPolicy(await setUpdatePolicy(next))
      toast.success(next ? "מנגנון העדכון הופעל" : "מנגנון העדכון הושבת")
    } catch (err) {
      toast.error(`השינוי נכשל: ${(err as Error).message}`)
    } finally {
      setBusy(false)
    }
  }

  const enabled = policy?.autoUpdateEnabled ?? true

  return (
    <Card className={!policy || enabled ? undefined : "border-amber-500"}>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          {enabled ? (
            <Power className="size-4" />
          ) : (
            <PowerOff className="size-4 text-amber-600 dark:text-amber-400" />
          )}
          מנגנון העדכון
          {policy && !enabled ? <Badge variant="destructive">מושבת</Badge> : null}
        </CardTitle>
        <Button
          size="xs"
          variant={enabled ? "destructive" : "default"}
          disabled={!policy || busy}
          onClick={() => toggle(!enabled)}
        >
          {enabled ? "השבת לכל המכשירים" : "הפעל מחדש"}
        </Button>
      </CardHeader>
      <CardContent className="text-sm text-muted-foreground">
        {!policy ? (
          "טוען…"
        ) : enabled ? (
          "המכשירים מושכים את המניפסט בחלון העדכון שלהם ומתקינים את הגרסה הנוכחית."
        ) : (
          <>
            המניפסט חוזר ריק - אף מכשיר לא יוריד גרסה חדשה, גם אם יש גרסה מסומנת.
            התקנה נקודתית ממסך המכשיר עוקפת את זה וממשיכה לעבוד.
          </>
        )}
        {policy?.updatedAt ? (
          <div className="mt-1 text-xs">
            שונה לאחרונה {formatAbsolute(policy.updatedAt)}
            {policy.updatedBy ? ` · ${policy.updatedBy}` : ""}
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

function UploadCard({
  busy,
  setBusy,
  onUploaded,
}: {
  busy: boolean
  setBusy: (v: boolean) => void
  onUploaded: () => Promise<void>
}) {
  const [file, setFile] = React.useState<File | null>(null)
  const [packageName, setPackageName] = React.useState<string>(ALLOWED_PACKAGES[0])
  const [version, setVersion] = React.useState("")
  const [versionCode, setVersionCode] = React.useState("")
  const [releaseNotes, setReleaseNotes] = React.useState("")
  const [progress, setProgress] = React.useState<UploadProgress | null>(null)
  const fileInput = React.useRef<HTMLInputElement>(null)
  const uploadRef = React.useRef<{ abort: () => void } | null>(null)

  // ההעלאה חיה ב-XHR, לא ב-React: אם עוזבים את העמוד באמצע, נבטל אותה
  // במקום להשאיר בקשה תלויה שתכתוב קובץ יתום בשרת.
  React.useEffect(() => () => uploadRef.current?.abort(), [])

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!file) {
      toast.error("בחר קובץ APK")
      return
    }
    if (!version.trim()) {
      toast.error("הזן מספר גרסה (למשל 1.7.7)")
      return
    }
    setBusy(true)
    setProgress({ loaded: 0, total: file.size, percent: 0 })
    const upload = uploadBuild(
      file,
      { packageName, version: version.trim(), versionCode, releaseNotes },
      setProgress,
    )
    uploadRef.current = upload
    try {
      const build = await upload
      toast.success(`${build.version} הועלתה. סמן אותה כנוכחית כדי להפיץ.`)
      setFile(null)
      setVersion("")
      setVersionCode("")
      setReleaseNotes("")
      if (fileInput.current) fileInput.current.value = ""
      await onUploaded()
    } catch (err) {
      const msg = (err as Error).message
      if (msg !== "aborted") {
        toast.error(
          msg === "not_an_apk"
            ? "הקובץ אינו APK תקין"
            : msg === "file_too_large"
              ? "הקובץ גדול מדי"
              : `ההעלאה נכשלה: ${msg}`,
        )
      }
    } finally {
      uploadRef.current = null
      setProgress(null)
      setBusy(false)
    }
  }

  function cancelUpload() {
    uploadRef.current?.abort()
    toast.message("ההעלאה בוטלה")
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="size-4" />
          העלאת APK
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form className="grid gap-3 sm:grid-cols-2" onSubmit={onSubmit}>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="apk">קובץ APK</Label>
            <Input
              id="apk"
              type="file"
              accept=".apk,application/vnd.android.package-archive"
              ref={fileInput}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            {file ? (
              <p className="text-xs text-muted-foreground">
                {file.name} · {formatBytes(file.size)}
              </p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pkg">חבילה</Label>
            <select
              id="pkg"
              dir="ltr"
              className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
              value={packageName}
              onChange={(e) => setPackageName(e.target.value)}
            >
              {ALLOWED_PACKAGES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="version">גרסה</Label>
            <Input
              id="version"
              dir="ltr"
              placeholder="1.7.7"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="versionCode">versionCode (אופציונלי)</Label>
            <Input
              id="versionCode"
              dir="ltr"
              inputMode="numeric"
              placeholder="177"
              value={versionCode}
              onChange={(e) => setVersionCode(e.target.value)}
            />
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="notes">הערות גרסה (אופציונלי)</Label>
            <Textarea
              id="notes"
              rows={2}
              value={releaseNotes}
              onChange={(e) => setReleaseNotes(e.target.value)}
            />
          </div>

          {progress ? (
            <div className="space-y-1.5 sm:col-span-2">
              <div className="bg-muted h-2 w-full overflow-hidden rounded-full">
                <div
                  className={
                    progress.percent === null
                      ? "bg-primary h-full w-full animate-pulse"
                      : "bg-primary h-full transition-[width] duration-150"
                  }
                  style={progress.percent === null ? undefined : { width: `${progress.percent}%` }}
                />
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {progress.percent === 100
                    ? // הבייטים עברו; השרת עדיין כותב לדיסק ומחשב sha256
                      "מעבד בשרת…"
                    : progress.percent === null
                      ? `הועלו ${formatBytes(progress.loaded)}`
                      : `${progress.percent}% · ${formatBytes(progress.loaded)} מתוך ${formatBytes(progress.total)}`}
                </span>
                <button
                  type="button"
                  className="hover:text-foreground underline"
                  onClick={cancelUpload}
                >
                  בטל
                </button>
              </div>
            </div>
          ) : null}

          <div className="sm:col-span-2">
            <Button type="submit" disabled={busy}>
              <Upload className="size-4" />
              {progress ? "מעלה…" : "העלה"}
            </Button>
            <p className="mt-2 text-xs text-muted-foreground">
              הגרסה לא מופצת לאף מכשיר עד שמסמנים אותה כ"נוכחית".
            </p>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
